import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, HelpHint } from '@wit-support/ui';
import { Check, CircleHelp, CircleX, Clock } from 'lucide-react';
import { discrepancyValueRu } from '../discrepancy-values.js';
import { objectLabel } from '../naming.js';
/** Мелкая приглушённая пометка рядом с пунктом («проверяется после создания»). */
function MutedTag({ children }) {
    return (_jsx(Badge, { variant: "outline", className: "whitespace-nowrap font-normal", children: children }));
}
/**
 * Строка пункта: значок и текст в ОДНОЙ строке, текст переносится сам (на
 * телефоне значок не отрывается от подписи).
 */
function GuaranteeLine({ icon, children, testId, state, }) {
    return (_jsxs("li", { className: "flex items-start gap-2 text-sm", "data-testid": testId, "data-state": state, children: [_jsx("span", { className: "mt-0.5 flex shrink-0", children: icon }), _jsx("span", { className: "min-w-0 flex-1", children: children })] }));
}
/**
 * Чек-лист «Гарантии» плана (спека §6): что мы принудительно выставляем —
 * утверждением («Анимация выключена»), нейтрально. Текст и пояснение —
 * от auto-ads (`text`, `note`); старый auto-ads без `text` — название из
 * реестра. Сверяемый пункт — пометка «проверяется после создания»;
 * объяснение — из реестра auto-ads.
 */
export function GuaranteesList({ rows, descriptors = [], }) {
    if (rows.length === 0)
        return null;
    const byKey = new Map(descriptors.map((d) => [d.key, d]));
    return (_jsx("ul", { className: "flex flex-col gap-1.5", "data-testid": "guarantees", children: rows.map((g) => {
            const d = byKey.get(g.key);
            const text = g.text ?? g.label;
            return (_jsx(GuaranteeLine, { testId: `plan-guarantee-${g.key}`, icon: _jsx(Check, { "aria-hidden": true, className: "h-4 w-4 text-[var(--color-text-secondary)]" }), children: _jsxs("span", { className: "inline-flex flex-wrap items-center gap-x-2 gap-y-0.5", children: [_jsxs("span", { "data-testid": `plan-guarantee-text-${g.key}`, children: [text, g.note != null ? (_jsxs("span", { className: "text-[var(--color-text-secondary)]", children: [" \u2014 ", g.note] })) : null] }), d && d.verify !== 'none' ? _jsx(MutedTag, { children: "\u043F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u0442\u0441\u044F \u043F\u043E\u0441\u043B\u0435 \u0441\u043E\u0437\u0434\u0430\u043D\u0438\u044F" }) : null, d?.detail ? _jsx(HelpHint, { label: `Подробнее: ${text}`, children: d.detail }) : null] }) }, g.key));
        }) }));
}
const STATE_VIEW = {
    ok: { icon: Check, className: 'text-[var(--color-text-secondary)]', text: 'соблюдена' },
    broken: { icon: CircleX, className: 'text-[var(--color-state-danger-fg)]', text: 'нарушена' },
    pending: {
        icon: Clock,
        className: 'text-[var(--color-text-muted)]',
        text: 'сверки ещё не было',
    },
    'by-design': {
        icon: Check,
        className: 'text-[var(--color-text-secondary)]',
        text: 'соблюдается при создании, не сверяется',
    },
};
/** Состояние, которого экран не знает (новое у auto-ads), — как есть, нейтрально. */
function stateView(state) {
    return (STATE_VIEW[state] ?? {
        icon: CircleHelp,
        className: 'text-[var(--color-text-muted)]',
        text: state,
    });
}
/** Состояния, у которых рядом с пунктом нужна приглушённая пометка. */
const TAGGED_STATES = new Set(['pending', 'by-design']);
/**
 * «Гарантии» запуска (экран «Запуск»): пункты и вердикты — от auto-ads
 * (`GET /launches/:id` → `guarantees`); каждый пункт — утверждением,
 * нейтрально; красный — только нарушенный, под ним расхождения, которые его
 * нарушили (значения словами по полю реестра).
 */
export function GuaranteeChecks({ items, fields, }) {
    if (items.length === 0)
        return null;
    return (_jsx("ul", { className: "flex flex-col gap-2", "data-testid": "launch-guarantees", children: items.map((g) => {
            const v = stateView(g.state);
            const Icon = v.icon;
            const text = g.text;
            const tagged = TAGGED_STATES.has(g.state) || !(g.state in STATE_VIEW);
            // соблюдена, но у её путей есть предупреждения — «соблюдена, есть замечания»
            const warnings = g.warnings ?? [];
            const withNotes = g.state === 'ok' && warnings.length > 0;
            return (_jsxs(GuaranteeLine, { testId: `guarantee-${g.key}`, state: g.state, icon: _jsx(Icon, { "aria-label": v.text, className: `h-4 w-4 ${v.className}` }), children: [_jsxs("span", { className: "inline-flex flex-wrap items-center gap-x-2 gap-y-0.5", children: [_jsxs("span", { className: g.state === 'broken' ? 'text-[var(--color-state-danger-fg)]' : undefined, children: [text, g.state === 'broken' ? ' — нарушено' : ''] }), tagged ? _jsx(MutedTag, { children: v.text }) : null, withNotes ? _jsx(MutedTag, { children: "\u0441\u043E\u0431\u043B\u044E\u0434\u0435\u043D\u0430, \u0435\u0441\u0442\u044C \u0437\u0430\u043C\u0435\u0447\u0430\u043D\u0438\u044F" }) : null, g.detail ? _jsx(HelpHint, { label: `Подробнее: ${text}`, children: g.detail }) : null] }), warnings.length > 0 ? (_jsx("ul", { className: "mt-0.5 flex flex-col gap-0.5 text-xs text-[var(--color-text-secondary)]", "data-testid": `guarantee-warnings-${g.key}`, children: warnings.map((d, i) => {
                            const desc = d.fieldKey != null ? (fields?.get(d.fieldKey) ?? null) : null;
                            return (_jsxs("li", { className: "[overflow-wrap:anywhere]", children: [objectLabel(d.kind, d.localRef), " \u00B7 ", d.label !== '' ? d.label : d.path, ": \u043E\u0436\u0438\u0434\u0430\u043B\u0438 ", discrepancyValueRu(d.expected, desc), ", \u043F\u043E\u043B\u0443\u0447\u0438\u043B\u0438", ' ', discrepancyValueRu(d.actual, desc)] }, `${d.localRef}-${d.path}-${i}`));
                        }) })) : null, g.related.length > 0 ? (_jsx("ul", { className: "mt-0.5 flex flex-col gap-0.5 text-xs text-[var(--color-state-danger-fg)]", children: g.related.map((d, i) => {
                            const desc = d.fieldKey != null ? (fields?.get(d.fieldKey) ?? null) : null;
                            return (_jsxs("li", { className: "[overflow-wrap:anywhere]", children: [objectLabel(d.kind, d.localRef), " \u00B7 ", d.label !== '' ? d.label : d.path, ": \u043E\u0436\u0438\u0434\u0430\u043B\u0438 ", discrepancyValueRu(d.expected, desc), ", \u043F\u043E\u043B\u0443\u0447\u0438\u043B\u0438", ' ', discrepancyValueRu(d.actual, desc)] }, `${d.localRef}-${d.path}-${i}`));
                        }) })) : null] }, g.key));
        }) }));
}
//# sourceMappingURL=guarantees-list.js.map